import user from "@/store/user";
import { BopModelEnum, BopStatus } from "@/utils/Enums";
import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import Bop from "./models/Bop";
import Feb from "./models/Feb";
import SubBop from "./models/SubBop";
import Validation from "./models/Validation";
import { Guid } from "guid-typescript";
import MinimumFacturableService from "./services/MinimumFacturableService";
const pkg = require("@/../package.json");

const vuexSession = new VuexPersistence<any>({
  storage: window.sessionStorage,
});

Vue.use(Vuex);

// Helper to aggregate totals across all FEBs/SubBOPs and update BOP totals
function aggregateTotals(state: any) {
  const bop: any = state.bop;
  const totals = { principal: 0, diverse: 0, suppHeure: 0, suppEpi: 0 };
  const febs: any[] = bop.Febs || bop.febs || [];
  febs.forEach((feb: any) => {
    (feb.subBops || []).forEach((sb: any) => {
      const signed = feb.visas.some((v) => {
        const key = v?.event?.event?.key;
        return (
          key === "Signature" ||
          key === 3 ||
          (typeof key === "string" && key.toLowerCase() === "signature")
        );
      });
      if (!signed && BopStatus.Finished.toString() === bop.bopStatusKey) {
        return;
      }
      sb.totalPoints = computeTotalPointsWithMinFacturable(
        sb,
        state.minimumFacturable,
      );
      totals.principal += sb.totalOperationPrincipalPoints ?? 0;
      totals.diverse += sb.totalOperationDiversePoints ?? 0;
      totals.suppHeure += sb.totalOperationSuppHeurePoints ?? 0;
      totals.suppEpi += sb.totalOperationSuppEpiPoints ?? 0;
    });
  });
  bop.totalOperationPrincipalPoints = totals.principal;
  bop.totalOperationDiversePoints = totals.diverse;
  bop.totalOperationSuppHeurePoints = totals.suppHeure;
  bop.totalOperationSuppEpiPoints = totals.suppEpi;
  bop.totalPoints =
    (bop.totalOperationPrincipalPoints ?? 0) +
    (bop.totalOperationDiversePoints ?? 0) +
    (bop.totalOperationSuppHeurePoints ?? 0) +
    (bop.totalOperationSuppEpiPoints ?? 0) +
    (bop.totalSuppHauteurPoints ?? 0);
}
function computeTotalPointsWithMinFacturable(sub: any, minimumFacturable: any) {
  const p = Number(sub.totalOperationPrincipalPoints ?? 0);
  const d = Number(sub.totalOperationDiversePoints ?? 0);
  const sh = Number(sub.totalOperationSuppHeurePoints ?? 0);
  const se = Number(sub.totalOperationSuppEpiPoints ?? 0);
  const hh = Number(sub.totalSuppHauteurPoints ?? 0);
  const min = Number(minimumFacturable?.minimumPoints ?? 0);

  if (minimumFacturable?.isPrincipalOnly) {
    // include 'diverse' when principal-only is true (your rule)
    const total = p < min ? min + d + sh + se + hh : p + d + sh + se + hh;
    return Math.round((total + Number.EPSILON) * 100) / 100;
  } else {
    const total = p + d < min ? min + sh + se + hh : p + d + sh + se + hh;
    return Math.round((total + Number.EPSILON) * 100) / 100;
  }
}

export default new Vuex.Store({
  state: {
    showExportLoader: false,
    inRouter: false,
    isLoggedIn: false,
    usedSitesTypeTravaux: {},
    hasBeenModified: false,
    isFirstCheckBoxTableLoad: true,
    bop: new Bop(),
    statuses: [],
    validation: new Validation(),
    selectedBopModel: {},
    bopOperationOptions: {},
    selectedGlobalCoefficients: {},
    multipleSelectedGlobalCoefficients: {},
    minimumFacturable: null as any,
    // Definitions of global coefficients for Operation Principal (per model)
    operationPrincipalGlobalCoefficients: {},
    selectedSubBopId: null,
    selectedFebId: null,
    // Holds a read-only snapshot of the BOP from archive when applicable
    bopArchive: null,
    // Environment-specific coefficient options filtered by site/typeTravaux/bopModel
    envSpecificOptions: [] as Array<{ id: string; coefficientId: string; value: number; label: string }>,
    bopStatuses: null,
    operationFunctions: {},
    isRowClickTemporaryDisabled: false,
    APP_VERSION: pkg.version || 0,
    isTranslatinLoaded: false,
  },
  mutations: {
    changeShowExportLoader(state: any, value: boolean) {
      state.showExportLoader = value;
    },
    changeInRouter(state: any, value: boolean) {
      state.inRouter = value;
    },
    changeStatuses(state: any, value: Array<any>) {
      state.statuses = value;
    },
    changeIsTranslationLoaded(state: any, value) {
      state.isTranslatinLoaded = value;
    },
    changeIsRowClickTemporaryDisabled(state: any, isRowClickTemporaryDisabled) {
      state.isRowClickTemporaryDisabled = isRowClickTemporaryDisabled;
    },
    changeOperationFuctions(state: any, operationFunctions) {
      const operationId = operationFunctions.id;
      const functionUnitPoints = operationFunctions.functionUnitPoints;
      const functionOperationUnitPoints =
        operationFunctions.functionOperationUnitPoints;
      state.operationFunctions[operationId] = {
        functionUnitPoints,
        functionOperationUnitPoints,
      };
    },
    loadBopStatuses(state: any, bopStatuses: any) {
      state.bopStatuses = bopStatuses;
    },
    updateFeb(state: any, feb: Feb) {
      state.bop.feb = feb;
    },
    changeTotalOperationDiversePoints(state: any, total: number) {
      const bop: any = state.bop;
      const febs: any[] = bop.Febs || bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const selectedSubBop =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || null
          : null;

      if (selectedSubBop) {
        selectedSubBop.totalOperationDiversePoints = total;
        aggregateTotals(state);
      } else {
        bop.totalOperationDiversePoints = total;
        bop.totalPoints =
          (bop.totalOperationPrincipalPoints ?? 0) +
          (bop.totalOperationDiversePoints ?? 0) +
          (bop.totalOperationSuppHeurePoints ?? 0) +
          (bop.totalOperationSuppEpiPoints ?? 0) +
          (bop.totalSuppHauteurPoints ?? 0);
      }
    },
    changetotalSuppHauteurPointsPoints(state: any, total: number) {
      const bop: Bop = state.bop;

      bop.totalSuppHauteurPoints = total;

      bop.totalPoints =
        bop.totalOperationPrincipalPoints +
        bop.totalOperationDiversePoints +
        bop.totalOperationSuppHeurePoints +
        bop.totalOperationSuppEpiPoints +
        bop.totalSuppHauteurPoints;
    },
    changeTotalOperationSuppHeurePoints(state: any, total: number) {
      const bop: any = state.bop;
      const febs: any[] = bop.Febs || bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const selectedSubBop =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || null
          : null;

      if (selectedSubBop) {
        selectedSubBop.totalOperationSuppHeurePoints = total;
        aggregateTotals(state);
      } else {
        bop.totalOperationSuppHeurePoints = total;
        bop.totalPoints =
          (bop.totalOperationPrincipalPoints ?? 0) +
          (bop.totalOperationDiversePoints ?? 0) +
          (bop.totalOperationSuppHeurePoints ?? 0) +
          (bop.totalOperationSuppEpiPoints ?? 0) +
          (bop.totalSuppHauteurPoints ?? 0);
      }
    },
    changeTotalOperationSuppEpiPoints(state: any, total: number) {
      const bop: any = state.bop;
      const febs: any[] = bop.Febs || bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const selectedSubBop =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || null
          : null;

      if (selectedSubBop) {
        selectedSubBop.totalOperationSuppEpiPoints = total;
        aggregateTotals(state);
      } else {
        bop.totalOperationSuppEpiPoints = total;
        bop.totalPoints =
          (bop.totalOperationPrincipalPoints ?? 0) +
          (bop.totalOperationDiversePoints ?? 0) +
          (bop.totalOperationSuppHeurePoints ?? 0) +
          (bop.totalOperationSuppEpiPoints ?? 0) +
          (bop.totalSuppHauteurPoints ?? 0);
      }
    },
    changeTotalOperationPrincipalPoints(state: any, total: number) {
      const bop: any = state.bop;
      const febs: any[] = bop.Febs || bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const selectedSubBop =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || null
          : null;

      if (selectedSubBop) {
        selectedSubBop.totalOperationPrincipalPoints = total;
        aggregateTotals(state);
      } else {
        bop.totalOperationPrincipalPoints = total;
        bop.totalPoints =
          (bop.totalOperationPrincipalPoints ?? 0) +
          (bop.totalOperationDiversePoints ?? 0) +
          (bop.totalOperationSuppHeurePoints ?? 0) +
          (bop.totalOperationSuppEpiPoints ?? 0) +
          (bop.totalSuppHauteurPoints ?? 0);
      }
    },
    removeBopOperationPrincipalGroup(state: any, operationGroupIndex: number) {
      if (operationGroupIndex === -1) return;
      const febs: any[] = state.bop.Febs || state.bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const container: any =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || state.bop
          : state.bop;
      let operationGroups = container.operationPrincipalGroups;

      // if (operationGroups[operationGroupIndex].isNew) {
      //   operationGroups.splice(operationGroupIndex, 1);
      // } else {
      operationGroups[operationGroupIndex].canBeDeleted = true;
      // }
    },
    removeBopOperationPrincipal(
      state: any,
      data: { operationGroupIndex: number; operationIndex: number },
    ) {
      const febs: any[] = state.bop.Febs || state.bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const container: any =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || state.bop
          : state.bop;
      const operationGroups = container.operationPrincipalGroups;
      operationGroups[data.operationGroupIndex].operations.splice(
        data.operationIndex,
        1,
      );
    },
    changeBopOperationPrincipal(
      state: any,
      data: { operationGroupIndex: number; operations: Array<any> },
    ) {
      const febs: any[] = state.bop.Febs || state.bop.febs || [];
      const selectedFeb = state.selectedFebId
        ? febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null
        : null;
      const container: any =
        state.selectedSubBopId && selectedFeb
          ? (selectedFeb.subBops || []).find(
              (sb: any) => sb && sb.id === state.selectedSubBopId,
            ) || state.bop
          : state.bop;
      const operationGroups = container.operationPrincipalGroups;
      if (operationGroups[data.operationGroupIndex]) {
        operationGroups[data.operationGroupIndex].operations = data.operations;
      }
    },
    changeSelectedBopModel(state: any, selectedBopModel: any) {
      state.selectedBopModel = selectedBopModel;
      if (
        selectedBopModel?.key === BopModelEnum.Echafaudage &&
        state.bop.feb == null
      ) {
        const newFeb = new Feb();
        newFeb.id = Guid.create().toString();
        state.bop.feb = newFeb;
      }
      if (
        selectedBopModel?.key === BopModelEnum.MultiEchafaudage &&
        (state.bop.febs == null || state.bop.febs.length === 0)
      ) {
        state.bop.febs = [];
        const newFeb = new Feb();
        newFeb.id = Guid.create().toString();
        const newSubBop = new SubBop();
        newSubBop.id = Guid.create().toString();
        newFeb.subBops = [newSubBop];
        state.bop.febs.push(newFeb);
      }
      // Ensure SubBOP selection is initialized early for MultiEchafaudage
      if (
        selectedBopModel &&
        selectedBopModel.key === BopModelEnum.MultiEchafaudage
      ) {
        const firstFeb = state.bop.febs[0] ?? null;
        const firstSubBop = firstFeb?.subBops?.[0] || null;
        state.selectedSubBopId = firstSubBop?.id || null;
        state.selectedFebId = firstFeb?.id || null;
        //   this.$store.commit("changeSelectedSubBopId", firstSubBop?.id || null);
        //   this.$store.commit("changeSelectedFebId", firstFeb?.id || null);
        // if (selectedBopModel && selectedBopModel.key === BopModelEnum.MultiEchafaudage) {
        //   const first = (state.feb[0].subBops && state.feb[0].subBops.length)
        //     ? state.feb[0].subBops[0].id
        //     : null;
        //   state.selectedSubBopId = first;
      } else {
        // Clear selection when leaving MultiEchafaudage
        state.selectedSubBopId = null;
      }
    },
    changeSelectedGlobalCoefficients(
      state: any,
      selectedGlobalCoefficients: any,
    ) {
      state.selectedGlobalCoefficients = selectedGlobalCoefficients;
    },
    changeOperationPrincipalGlobalCoefficients(state: any, defs: any) {
      state.operationPrincipalGlobalCoefficients = defs || {};
    },
    changeSelectedSubBopId(state: any, id: string | null) {
      state.selectedSubBopId = id;
    },
    changeSelectedFebId(state: any, id: string | null) {
      state.selectedFebId = id;
    },
    changeMultipleSelectedGlobalCoefficients(
      state: any,
      multipleSelectedGlobalCoefficients: any,
    ) {
      state.multipleSelectedGlobalCoefficients =
        multipleSelectedGlobalCoefficients;
    },
    changeEnvSpecificOptions(state: any, options: Array<{ id: string; coefficientId: string; value: number; label: string }>) {
      state.envSpecificOptions = options;
    },
    changeBopOperationOptions(state: any, config: any) {
      if (!config) {
        state.bopOperationOptions = {};
        return;
      }
      const operationGroupId = config.operationGroupId;
      const bopOperationOptions = config.bopOperationOptions;
      state.bopOperationOptions[operationGroupId] = bopOperationOptions;
    },
    changeBop(state: any, bop: Bop) {
      state.bop = bop;
    },
    changeBopArchive(state: any, archive: any) {
      state.bopArchive = archive;
    },
    changeValidation(state: any, validation: Validation) {
      state.validation = validation;
    },
    changeHasBeenModified(state: any, bool: boolean) {
      state.hasBeenModified = bool;
    },
    changeIsFirstCheckBoxTableLoad(state: any, bool: boolean) {
      state.isFirstCheckBoxTableLoad = bool;
    },
    resetUsedSitesTypeTravaux(state: any) {
      state.usedSitesTypeTravaux = {};
    },
    changeUsedSitesTypeTravaux(state: any, data: any) {
      const siteId = data.siteId;
      const typeTravauxId = data.typeTravauxId;
      const operationCode = data.operationCode;
      if (!state.usedSitesTypeTravaux[operationCode])
        state.usedSitesTypeTravaux[operationCode] = {};
      state.usedSitesTypeTravaux[operationCode][`${siteId}_${typeTravauxId}`] =
        data.id;
    },
    removeTypeTravauxFromchangeUsedSitesTypeTravaux(state: any, data: any) {
      const siteId = data.siteId;
      const typeTravauxId = data.typeTravauxId;
      const operationCode = data.operationCode;
      if (
        state.usedSitesTypeTravaux[operationCode] &&
        state.usedSitesTypeTravaux[operationCode][`${siteId}_${typeTravauxId}`]
      ) {
        delete state.usedSitesTypeTravaux[operationCode][
          `${siteId}_${typeTravauxId}`
        ];
      }
    },
    changeMinimumFacturable(state, value: any) {
      state.minimumFacturable = value;
    },
  },
  getters: {
    // Returns a short label for the BOP including selected FEB and SubBOP indices if applicable
    multiBopShort(state: any): string {
      const bop: any = state.bop;
      if (!bop) return "";
      const sitePart = `${bop.siteName || ""}`;
      const modelPart = `${(bop.bopModelName || "").toString().toLowerCase()}`;
      let numberPart = bop.bopNumber ? ` # ${bop.bopNumber}` : "";
      numberPart = bop.bopNumber ? ` # ${bop.bopNumber}` : " ?";
      const baseLabel = `${sitePart} - ${modelPart}${numberPart}`;
      const selectedFebId: string | null = state.selectedFebId || null;
      const selectedSubBopId: string | null = state.selectedSubBopId || null;
      const febs: any[] = bop.Febs || bop.febs || [];
      const selectedFeb =
        febs.find((f: any) => (f?.id || f) === selectedFebId) || {};
      const subBops: any[] = selectedFeb.subBops || [];
      const selectedSubBop =
        subBops.find((sb: any) => sb?.id === selectedSubBopId) || {};
      const febIndex = selectedFeb?.index ?? null;
      const febIndexString = febIndex != null ? String(febIndex) : "";
      const subBopIndex = selectedSubBop?.sequenceOrder ?? "";
      let result = baseLabel;
      if (febIndexString) result += `.${febIndexString}`;
      if (subBopIndex !== "") result += `.${subBopIndex}`;
      return result;
    },
    minimumFacturable(state): number {
      return state.minimumFacturable?.minimumPoints ?? 0;
    },
    minimumFacturablePrincipalOnly(state): boolean {
      return state.minimumFacturable?.isPrincipalOnly ?? false;
    },
    bopShort(state: any): string {
      const bop: any = state.bop;
      if (!bop) return "";
      const sitePart = `${bop.siteName || ""}`;
      const modelPart = `${(bop.bopModelName || "").toString().toLowerCase()}`;
      let numberPart = bop.bopNumber ? ` # ${bop.bopNumber}` : "";
      return `${sitePart} - ${modelPart}${numberPart}`;
    },
    selectedFeb(state: any): Feb | null {
      if (!state.selectedFebId) return null;
      const febs: any[] = state.bop.febs || [];
      const selectedFeb =
        febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null;
      return selectedFeb;
    },
    selectedSubBop(state: any): SubBop | null {
      if (!state.selectedSubBopId || !state.selectedFebId) return null;
      const febs: any[] = state.bop.febs || [];
      const selectedFeb =
        febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null;
      const subBops: any[] = selectedFeb?.subBops || [];
      const selectedSubBop =
        subBops.find((sb: any) => sb && sb.id === state.selectedSubBopId) ||
        null;
      return selectedSubBop;
    },
    selectedSubBopArchive(state: any): any | null {
      const archive = state.bopArchive;
      if (!archive) return null;
      if (!state.selectedSubBopId || !state.selectedFebId) return archive;
      const febs: any[] = archive.febs || [];
      const selectedFeb =
        febs.find((f: any) => (f?.id || f) === state.selectedFebId) || null;
      const subBops: any[] = selectedFeb?.subBops || [];
      const selected =
        subBops.find((sb: any) => sb && sb.id === state.selectedSubBopId) ||
        null;
      return selected || archive;
    },
    //Compute total points for a Bop or a SubBOP including minimum facturable logic
    computeTotalWithMinFacturable: (state) => (sub: any) => {
      return computeTotalPointsWithMinFacturable(sub, state.minimumFacturable);
    },
    calculateBopTotal: (state, getters) => {
      const bop: any = state.bop;
      if (!bop) return 0;
      if (bop.bopModelKey != BopModelEnum.MultiEchafaudage) {
        return Number(getters.computeTotalWithMinFacturable(bop) || 0);
      }
      const febs: any[] = bop.Febs || bop.febs || [];
      let total = 0;
      febs.forEach((feb: any) => {
        const signed = (feb.visas || []).some((v: any) => {
          const key = v?.event?.event?.key;
          return (
            key === "Signature" ||
            key === 3 ||
            (typeof key === "string" && key.toLowerCase() === "signature")
          );
        });
        if (!signed && BopStatus.Finished.toString() === bop.bopStatusKey)
          return;
        (feb.subBops || []).forEach((sb: any) => {
          total += Number(getters.computeTotalWithMinFacturable(sb) || 0);
        });
      });
      return Math.round((total + Number.EPSILON) * 100) / 100;
    },
  },
  actions: {
    async fetchMinimumFacturable({ commit, state }) {
      const { bopModelId, entrepriseId, typeTravaux } = state.bop;
      if (!bopModelId || !entrepriseId || !typeTravaux) {
        commit("changeMinimumFacturable", null);
        return;
      }
      try {
        const response =
          await MinimumFacturableService.getByEntrepriseBopModelDomainApplication(
            bopModelId,
            entrepriseId,
            typeTravaux,
          );
        commit("changeMinimumFacturable", response.data);
      } catch {
        commit("changeMinimumFacturable", null);
      }
    },
  },
  modules: {
    userModule: user,
  },
  plugins: [vuexSession.plugin],
});
